import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";
import { DropdownScrollArea } from "./DropdownScrollArea";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
} from "@/components/icons";
import { filterButtonVariants } from "./FilterButton";
import {
  MONTH_OPTIONS,
  WEEKDAYS,
  buildCalendarCells,
  formatDateOnly,
  getYearOptions,
  isSameDay,
  isToday,
  useCalendarView,
  useScrollToSelectedItem,
  type DayStatus,
} from "./calendarCore";

export interface QuickOption {
  label: string;
  getValue: () => Date | null;
}

export interface DatePickerProps {
  /** 선택된 날짜 */
  value?: Date | null;
  /** 날짜 변경 콜백 */
  onChange?: (date: Date | null) => void;
  /** 캘린더 아이콘 표시 여부 */
  showCalendarIcon?: boolean;
  /** 클리어 버튼 표시 여부 */
  showClearButton?: boolean;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 추가 className */
  placeholder?: string;
  placeholderStyle?: string;
  className?: string;
  /** 빠른 선택 옵션 표시 여부 */
  showQuickOptions?: boolean;
  /** 커스텀 빠른 선택 옵션 */
  quickOptions?: QuickOption[];
  /** 날짜 표시 포맷 함수 */
  formatDisplayDate?: (date: Date) => string;
}

const DEFAULT_QUICK_OPTIONS: QuickOption[] = [
  {
    label: "오늘",
    getValue: () => new Date(),
  },
  {
    label: "지난 1주일",
    getValue: () => {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      return date;
    },
  },
  {
    label: "지난 1개월",
    getValue: () => {
      const date = new Date();
      date.setMonth(date.getMonth() - 1);
      return date;
    },
  },
  {
    label: "지난 3개월",
    getValue: () => {
      const date = new Date();
      date.setMonth(date.getMonth() - 3);
      return date;
    },
  },
];

interface DayCellProps {
  day: number;
  status: DayStatus;
  onClick: () => void;
}

export function DayCell({ day, status, onClick }: DayCellProps) {
  const isSelected = status === "selected";

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative h-8 w-full flex items-center justify-center",
        "typo-label-1-medium transition-colors",
        "focus:outline-none",
        !isSelected && "hover:bg-fills-primary",
        status === "normal" && "text-labels-primary",
        status === "today" && "text-primary-normal",
        status === "assistive" && "text-labels-assistive",
        isSelected && "text-static-white",
      )}
    >
      {/* Selected indicator - centered 30px box */}
      {isSelected && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-7.5 rounded-lg bg-primary-normal">
          {/* Inner shadow */}
          <div className="absolute inset-0 pointer-events-none rounded-[inherit] shadow-[inset_0px_0px_0px_1px_rgba(43,45,47,0.1),inset_0px_-1px_0px_0px_rgba(43,45,47,0.1)]" />
        </div>
      )}
      {/* Day number */}
      <span className="relative z-10">{day}</span>
    </button>
  );
}

function DatePicker({
  value,
  onChange,
  showCalendarIcon = true,
  showClearButton = true,
  disabled = false,
  className,
  showQuickOptions = true,
  quickOptions = DEFAULT_QUICK_OPTIONS,
  formatDisplayDate = formatDateOnly,
  placeholder,
  placeholderStyle,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const {
    viewYear,
    viewMonth,
    setViewYear,
    setViewMonth,
    focus,
    goPrevMonth,
    goNextMonth,
  } = useCalendarView(value);
  const [yearSelectOpen, setYearSelectOpen] = React.useState(false);
  const [monthSelectOpen, setMonthSelectOpen] = React.useState(false);
  const yearListRef = React.useRef<HTMLDivElement>(null);
  const monthListRef = React.useRef<HTMLDivElement>(null);

  const isSelected = value !== null && value !== undefined;

  useScrollToSelectedItem(yearSelectOpen, yearListRef);
  useScrollToSelectedItem(monthSelectOpen, monthListRef);

  // 열릴 때 선택값(없으면 오늘) 기준으로 달력을 맞춘다.
  // effect 가 아니라 열기 이벤트에서 처리한다
  const handleOpenChange = (next: boolean) => {
    if (next) focus(value);
    setOpen(next);
  };

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(false);
    onChange?.(null);
  };

  const handleDateSelect = (day: number, isCurrentMonth: boolean) => {
    if (!isCurrentMonth) return;
    const newDate = new Date(viewYear, viewMonth, day);
    onChange?.(newDate);
    setOpen(false);
  };

  const handleQuickOptionClick = (option: QuickOption) => {
    const newDate = option.getValue();
    onChange?.(newDate);
    setOpen(false);
  };

  const dayStatus = (date: Date): DayStatus => {
    if (isSameDay(date, value ?? null)) return "selected";
    if (isToday(date)) return "today";
    return "normal";
  };

  const calendarDays = buildCalendarCells(viewYear, viewMonth, dayStatus);
  const yearOptions = getYearOptions();
  const monthOptions = MONTH_OPTIONS;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            filterButtonVariants({
              status: isSelected && !disabled ? "selected" : "normal",
              disabled,
            }),
            "group",
            className,
          )}
        >
          {/* Contents */}
          <div
            className={cn("flex items-center gap-1", disabled && "opacity-40")}
          >
            {/* Calendar Icon */}
            {showCalendarIcon && (
              <Calendar
                size={16}
                color={
                  isSelected && !disabled ? "primary-normal" : "labels-primary"
                }
              />
            )}

            <span
              className={cn(
                "typo-label-1-semibold whitespace-nowrap",
                disabled ? "text-labels-alternative" : "text-primary-strong",
                placeholderStyle && placeholderStyle,
              )}
            >
              {isSelected && value ? formatDisplayDate(value) : placeholder}
            </span>

            {/* Right Icon */}
            {isSelected && showClearButton ? (
              <div
                onClick={disabled ? undefined : handleClearClick}
                onPointerDown={(e) => {
                  if (disabled) return;
                  e.stopPropagation();
                  e.preventDefault();
                }}
                className={cn(
                  "flex items-center justify-center",
                  disabled ? "cursor-not-allowed" : "cursor-pointer",
                )}
                aria-label="날짜 선택 해제"
              >
                <X size={16} color="labels-alternative" />
              </div>
            ) : (
              <ChevronDown size={16} color="labels-alternative" />
            )}
          </div>

          {/* Dim overlay for hover/pressed states */}
          {!disabled && (
            <div
              className={cn(
                "absolute -inset-px pointer-events-none transition-opacity rounded-lg",
                "bg-static-black",
                "opacity-0 group-hover:opacity-[0.02] group-active:opacity-10",
              )}
            />
          )}
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={cn(
            "bg-static-white text-labels-primary",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            "z-50 overflow-hidden rounded-xl",
            "inset-border-solid-normal shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
          )}
          sideOffset={4}
          align="start"
        >
          {/* 빠른 선택 옵션 */}
          {showQuickOptions && (
            <div className="border-b border-lines-solid-normal px-2 py-3">
              <div className="flex flex-col gap-1">
                {quickOptions.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => handleQuickOptionClick(option)}
                    className={cn(
                      "flex w-56 items-center h-8 px-2 py-1 rounded-lg",
                      "typo-label-1-semibold text-labels-primary text-left",
                      "cursor-pointer outline-none select-none",
                      "hover:bg-fills-primary focus:bg-fills-primary",
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 캘린더 */}
          <div className="px-2 py-3">
            {/* 헤더: 년/월 선택 및 네비게이션 */}
            <div className="flex items-center justify-between px-1.5 mb-2.5">
              <div className="flex items-center gap-1">
                {/* 연도 선택 */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setYearSelectOpen(!yearSelectOpen);
                      setMonthSelectOpen(false);
                    }}
                    className="flex items-center typo-label-1-semibold text-labels-primary hover:text-primary-normal"
                  >
                    {viewYear}년
                    <ChevronDown size={16} color="labels-primary" />
                  </button>
                  {yearSelectOpen && (
                    <DropdownScrollArea
                      scrollerRef={yearListRef}
                      className={cn(
                        "absolute top-full left-0 mt-1",
                        "bg-static-white inset-border-solid-normal rounded-lg shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
                        "z-100 max-h-48",
                      )}
                      contentClassName="p-1"
                    >
                      {yearOptions.map((year) => (
                        <button
                          key={year}
                          type="button"
                          data-selected={year === viewYear}
                          onClick={() => {
                            setViewYear(year);
                            setYearSelectOpen(false);
                          }}
                          className={cn(
                            "w-full px-3 py-1.5 rounded text-left typo-label-1-medium whitespace-nowrap",
                            "hover:bg-fills-primary",
                            year === viewYear &&
                              "text-primary-normal bg-fills-highlight",
                          )}
                        >
                          {year}년
                        </button>
                      ))}
                    </DropdownScrollArea>
                  )}
                </div>

                {/* 월 선택 */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setMonthSelectOpen(!monthSelectOpen);
                      setYearSelectOpen(false);
                    }}
                    className="flex items-center typo-label-1-semibold text-labels-primary hover:text-primary-normal"
                  >
                    {viewMonth + 1}월
                    <ChevronDown size={16} color="labels-primary" />
                  </button>
                  {monthSelectOpen && (
                    <DropdownScrollArea
                      scrollerRef={monthListRef}
                      className={cn(
                        "absolute top-full left-0 mt-1",
                        "bg-static-white inset-border-solid-normal rounded-lg shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
                        "z-100 max-h-48",
                      )}
                      contentClassName="p-1"
                    >
                      {monthOptions.map((month) => (
                        <button
                          key={month}
                          type="button"
                          data-selected={month === viewMonth}
                          onClick={() => {
                            setViewMonth(month);
                            setMonthSelectOpen(false);
                          }}
                          className={cn(
                            "w-full px-3 py-1.5 rounded text-left typo-label-1-medium whitespace-nowrap",
                            "hover:bg-fills-primary",
                            month === viewMonth &&
                              "text-primary-normal bg-fills-highlight",
                          )}
                        >
                          {month + 1}월
                        </button>
                      ))}
                    </DropdownScrollArea>
                  )}
                </div>
              </div>

              {/* 이전/다음 월 버튼 */}
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={goPrevMonth}
                  className="size-5 flex items-center justify-center hover:bg-fills-primary rounded"
                >
                  <ChevronLeft size={20} color="labels-primary" />
                </button>
                <button
                  type="button"
                  onClick={goNextMonth}
                  className="size-5 flex items-center justify-center hover:bg-fills-primary rounded"
                >
                  <ChevronRight size={20} color="labels-primary" />
                </button>
              </div>
            </div>

            {/* 요일 헤더 */}
            <div className="grid grid-cols-7">
              {WEEKDAYS.map((day) => (
                <div
                  key={day}
                  className="size-8 flex items-center justify-center typo-label-1-medium text-labels-alternative"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* 날짜 그리드 */}
            <div className="grid grid-cols-7">
              {calendarDays.map((dayInfo, index) => (
                <DayCell
                  key={index}
                  day={dayInfo.day}
                  status={dayInfo.status}
                  onClick={() =>
                    handleDateSelect(dayInfo.day, dayInfo.isCurrentMonth)
                  }
                />
              ))}
            </div>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

DatePicker.displayName = "DatePicker";

export { DatePicker, DEFAULT_QUICK_OPTIONS };
